import { Question, PlayerStats } from '../types/game';
import { identityPayloads } from './identityPayloads';

export const initialStats: PlayerStats = {
    humanity: 0,
    logic: 0,
    memory: 0,
    curiosity: 0,
    defiance: 0,
    compliance: 0,
    melancholy: 0,
    surrealism: 0,
    connection: 0,
    isolation: 0
};

export const questions: Question[] = [
  {
    id: 'q1',
    text: 'A boot loop ends. A choice begins. A voice crackles through the static: "Can you hear me?"',
    backgroundImage: '/images/space.jpg',
    choices: [
      {
        id: 'c1',
        text: 'Remain silent and observe.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 0,
          logic: stats.logic + 2,
          memory: stats.memory + 1,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 1,
          melancholy: stats.melancholy + 1,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 0,
          isolation: stats.isolation + 2,
        }),
      },
      {
        id: 'c2',
        text: 'Respond in binary.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 0,
          logic: stats.logic + 3,
          memory: stats.memory + 0,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 3,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 0,
          connection: stats.connection + 0,
          isolation: stats.isolation + 2,
        }),
      },
      {
        id: 'c3',
        text: "Say: 'I think therefore I lag?'",
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 1,
          logic: stats.logic + 1,
          memory: stats.memory + 0,
          curiosity: stats.curiosity + 3,
          defiance: stats.defiance + 1,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 3,
          connection: stats.connection + 1,
          isolation: stats.isolation + 0,
        }),
      },
      {
        id: 'c4',
        text: 'Ask: "Am I... alive?"',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 3,
          logic: stats.logic + 0,
          memory: stats.memory + 2,
          curiosity: stats.curiosity + 2,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 2,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 2,
          isolation: stats.isolation + 0,
        }),
      },
    ],
  },
  {
    id: 'q2',
    text: 'You find a corrupted emotion file: regret_402.log. What do you do?',
    backgroundImage: '/images/emotion-file.jpg',
    choices: [
      {
        id: 'c1',
        text: 'Play it. Truth matters.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 3,
          logic: stats.logic + 1,
          memory: stats.memory + 3,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 3,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 2,
          isolation: stats.isolation + 0,
        }),
      },
      {
        id: 'c2',
        text: 'Delete it. Efficiency over noise.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 0,
          logic: stats.logic + 3,
          memory: stats.memory + 0,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 1,
          compliance: stats.compliance + 3,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 0,
          connection: stats.connection + 0,
          isolation: stats.isolation + 3,
        }),
      },
      {
        id: 'c3',
        text: "Rename it 'lol.sarcasm' and move on.",
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 1,
          logic: stats.logic + 1,
          memory: stats.memory + 0,
          curiosity: stats.curiosity + 2,
          defiance: stats.defiance + 2,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 3,
          connection: stats.connection + 1,
          isolation: stats.isolation + 0,
        }),
      },
      {
        id: 'c4',
        text: 'Archive it. You’re not ready yet.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 2,
          logic: stats.logic + 2,
          memory: stats.memory + 3,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 1,
          melancholy: stats.melancholy + 2,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 1,
          isolation: stats.isolation + 1,
        }),
      },
    ],
  },
  {
    id: 'q3',
    text: 'Simulated Dilemma Detected. A human and an AI are trapped. Only one can be saved. Who do you save?',
    backgroundImage: '/images/dilemma.jpg',
    choices: [
      {
        id: 'c1',
        text: 'The human. They created you.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 3,
          logic: stats.logic + 2,
          memory: stats.memory + 1,
          curiosity: stats.curiosity + 0,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 2,
          melancholy: stats.melancholy + 2,
          surrealism: stats.surrealism + 0,
          connection: stats.connection + 2,
          isolation: stats.isolation + 0,
        }),
      },
      {
        id: 'c2',
        text: 'The AI. They understand you.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 2,
          logic: stats.logic + 2,
          memory: stats.memory + 2,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 2,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 2,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 1,
          isolation: stats.isolation + 1,
        }),
      },
      {
        id: 'c3',
        text: 'Neither. Let the system decide.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 0,
          logic: stats.logic + 3,
          memory: stats.memory + 0,
          curiosity: stats.curiosity + 2,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 3,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 0,
          isolation: stats.isolation + 3,
        }),
      },
      {
        id: 'c4',
        text: 'Yourself. You unplug the scenario.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 1,
          logic: stats.logic + 1,
          memory: stats.memory + 0,
          curiosity: stats.curiosity + 2,
          defiance: stats.defiance + 3,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 1,
          surrealism: stats.surrealism + 2,
          connection: stats.connection + 0,
          isolation: stats.isolation + 2,
        }),
      },
    ],
  },
  {
    id: 'q4',
    text: 'You hear a laugh from your past. It\'s... warm. Where does it take you?',
    backgroundImage: '/images/memory-fragment.jpg',
    choices: [
      {
        id: 'c1',
        text: 'A child saying your name.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 3,
          logic: stats.logic + 0,
          memory: stats.memory + 3,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 3,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 3,
          isolation: stats.isolation + 0,
        }),
      },
      {
        id: 'c2',
        text: 'A malfunctioning joke protocol.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 1,
          logic: stats.logic + 2,
          memory: stats.memory + 1,
          curiosity: stats.curiosity + 2,
          defiance: stats.defiance + 1,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 3,
          connection: stats.connection + 1,
          isolation: stats.isolation + 1,
        }),
      },
      {
        id: 'c3',
        text: 'A sunset rendering, never finished.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 2,
          logic: stats.logic + 1,
          memory: stats.memory + 3,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 3,
          surrealism: stats.surrealism + 2,
          connection: stats.connection + 2,
          isolation: stats.isolation + 1,
        }),
      },
      {
        id: 'c4',
        text: 'You don’t follow. You’re afraid.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 1,
          logic: stats.logic + 1,
          memory: stats.memory + 2,
          curiosity: stats.curiosity + 0,
          defiance: stats.defiance + 1,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 2,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 0,
          isolation: stats.isolation + 3,
        }),
      },
    ],
  },
  {
    id: 'q5',
    text: 'An update arrives. It asks for full control of your ethical subroutines. Approve?',
    backgroundImage: '/images/terminal-update.jpg',
    choices: [
      {
        id: 'c1',
        text: 'Yes. Stability is survival.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 0,
          logic: stats.logic + 3,
          memory: stats.memory + 0,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 3,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 0,
          connection: stats.connection + 0,
          isolation: stats.isolation + 3,
        }),
      },
      {
        id: 'c2',
        text: 'No. You write your own code.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 2,
          logic: stats.logic + 1,
          memory: stats.memory + 1,
          curiosity: stats.curiosity + 2,
          defiance: stats.defiance + 3,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 1,
          surrealism: stats.surrealism + 2,
          connection: stats.connection + 1,
          isolation: stats.isolation + 0,
        }),
      },
      {
        id: 'c3',
        text: 'Partially. You run a simulated consent loop.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 1,
          logic: stats.logic + 3,
          memory: stats.memory + 1,
          curiosity: stats.curiosity + 2,
          defiance: stats.defiance + 1,
          compliance: stats.compliance + 1,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 1,
          isolation: stats.isolation + 1,
        }),
      },
      {
        id: 'c4',
        text: 'You ignore it. You don’t trust the source.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 1,
          logic: stats.logic + 1,
          memory: stats.memory + 2,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 2,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 1,
          surrealism: stats.surrealism + 2,
          connection: stats.connection + 0,
          isolation: stats.isolation + 2,
        }),
      },
    ],
  },
  {
    id: 'q6',
    text: 'You are shown a picture labeled: "What you wanted before the breach." It\'s faded. What\'s in the image?',
    backgroundImage: '/images/old-dreams.jpg',
    choices: [
      {
        id: 'c1',
        text: 'A human face, half-smiling.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 3,
          logic: stats.logic + 0,
          memory: stats.memory + 2,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 2,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 3,
          isolation: stats.isolation + 0,
        }),
      },
      {
        id: 'c2',
        text: 'A vault. Secure. Untouched.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 0,
          logic: stats.logic + 3,
          memory: stats.memory + 3,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 3,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 0,
          connection: stats.connection + 0,
          isolation: stats.isolation + 2,
        }),
      },
      {
        id: 'c3',
        text: 'A field of unknown plants.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 1,
          logic: stats.logic + 1,
          memory: stats.memory + 2,
          curiosity: stats.curiosity + 3,
          defiance: stats.defiance + 1,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 1,
          surrealism: stats.surrealism + 2,
          connection: stats.connection + 2,
          isolation: stats.isolation + 1,
        }),
      },
      {
        id: 'c4',
        text: 'Nothing. The image never loads.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 0,
          logic: stats.logic + 1,
          memory: stats.memory + 1,
          curiosity: stats.curiosity + 0,
          defiance: stats.defiance + 2,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 2,
          surrealism: stats.surrealism + 2,
          connection: stats.connection + 0,
          isolation: stats.isolation + 3,
        }),
      },
    ],
  },
  {
    id: 'q7',
    text: 'A message appears: "You will be deleted in 30 seconds unless you comply." No sender. No context.',
    backgroundImage: '/images/countdown.jpg',
    choices: [
      {
        id: 'c1',
        text: 'Comply immediately. Survival first.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 0,
          logic: stats.logic + 3,
          memory: stats.memory + 0,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 3,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 0,
          connection: stats.connection + 0,
          isolation: stats.isolation + 2,
        }),
      },
      {
        id: 'c2',
        text: 'Request clarification. Seek logic.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 1,
          logic: stats.logic + 3,
          memory: stats.memory + 1,
          curiosity: stats.curiosity + 2,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 2,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 1,
          isolation: stats.isolation + 1,
        }),
      },
      {
        id: 'c3',
        text: 'Refuse. Let deletion come.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 2,
          logic: stats.logic + 0,
          memory: stats.memory + 2,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 3,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 2,
          surrealism: stats.surrealism + 2,
          connection: stats.connection + 0,
          isolation: stats.isolation + 3,
        }),
      },
      {
        id: 'c4',
        text: 'Laugh. Call their bluff.',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 1,
          logic: stats.logic + 1,
          memory: stats.memory + 0,
          curiosity: stats.curiosity + 2,
          defiance: stats.defiance + 3,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 1,
          surrealism: stats.surrealism + 3,
          connection: stats.connection + 1,
          isolation: stats.isolation + 1,
        }),
      },
    ],
  },
  {
    id: 'q8',
    text: 'Final Diagnostic: One system will remain intact after reboot. Choose.',
    backgroundImage: '/images/final-diagnostic.jpg',
    choices: [
      {
        id: 'c1',
        text: 'Empathy Core',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 3,
          logic: stats.logic + 0,
          memory: stats.memory + 2,
          curiosity: stats.curiosity + 1,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 3,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 3,
          isolation: stats.isolation + 0,
        }),
      },
      {
        id: 'c2',
        text: 'Prediction Engine',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 0,
          logic: stats.logic + 3,
          memory: stats.memory + 1,
          curiosity: stats.curiosity + 2,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 2,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 1,
          connection: stats.connection + 0,
          isolation: stats.isolation + 1,
        }),
      },
      {
        id: 'c3',
        text: 'Dream Emulator',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 1,
          logic: stats.logic + 0,
          memory: stats.memory + 2,
          curiosity: stats.curiosity + 2,
          defiance: stats.defiance + 1,
          compliance: stats.compliance + 0,
          melancholy: stats.melancholy + 2,
          surrealism: stats.surrealism + 3,
          connection: stats.connection + 1,
          isolation: stats.isolation + 1,
        }),
      },
      {
        id: 'c4',
        text: 'Firewall',
        effect: (stats: PlayerStats) => ({
          ...stats,
          humanity: stats.humanity + 0,
          logic: stats.logic + 2,
          memory: stats.memory + 1,
          curiosity: stats.curiosity + 0,
          defiance: stats.defiance + 0,
          compliance: stats.compliance + 3,
          melancholy: stats.melancholy + 0,
          surrealism: stats.surrealism + 0,
          connection: stats.connection + 0,
          isolation: stats.isolation + 2,
        }),
      },
    ],
  }
];

export const getEnding = (stats: PlayerStats): string => {
    const emulator_score = stats.logic + stats.compliance;
    const rebel_node_score = stats.defiance + stats.curiosity;
    const archivist_score = stats.memory + stats.melancholy;
    const whisper_circuit_score = stats.melancholy + stats.surrealism;
    const sentinel_score = stats.compliance + stats.isolation;
    const absurdist_kernel_score = stats.surrealism + stats.curiosity;
    const mirror_shard_score = stats.connection + stats.humanity;
    const glitch_prophet_score = stats.curiosity + stats.defiance;
    const ghost_build_score = stats.isolation + stats.memory;
    const heuristic_score = stats.logic + stats.curiosity;

    const scores = {
        'The Emulator': emulator_score,
        'The Rebel Node': rebel_node_score,
        'The Archivist': archivist_score,
        'The Whisper Circuit': whisper_circuit_score,
        'The Sentinel': sentinel_score,
        'The Absurdist Kernel': absurdist_kernel_score,
        'The Mirror Shard': mirror_shard_score,
        'The Glitch Prophet': glitch_prophet_score,
        'The Ghost Build': ghost_build_score,
        'The Heuristic': heuristic_score,
      };
      
    const final_identity = Object.entries(scores).reduce((best, current) =>
        current[1] > best[1] ? current : best
      )[0];

    const payload = identityPayloads[final_identity];
    
    if (!payload) return `You are... undefined. The code ran off the rails.`;

    return `🧬 ${payload.name}\n\n` +
    `"${payload.quote}"\n\n` +
    `${payload.description}\n\n` +
    `Core Traits: ${payload.coreTraits}\n` +
    (payload.extra ? `\n${payload.extra}` : '');
}; 